import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import SupervisorPinModal from "@/components/SupervisorPinModal";
import { AlertTriangle, History, RotateCcw } from "lucide-react";

/**
 * Admin-only stock-take reset. Zeroes `remaining` on every goods intake
 * (keeping history for traceability) so the bakery can re-enter today's
 * physical stock as fresh new intakes. Behind an admin PIN gate on top
 * of the admin-only backend route — destructive enough to warrant both.
 */
export default function StockResetPanel() {
  const [pinOpen, setPinOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const doReset = async () => {
    setBusy(true);
    try {
      const r = await api.post("/intakes/stock-reset");
      setLastResult(r.data);
      toast.success(
        `Stock reset · ${r.data.updated} intake rows set to 0. History preserved.`
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="panel border-rose-200" data-testid="stock-reset-panel">
      <div className="panel-header bg-rose-50/60 dark:bg-rose-950/20">
        <div className="font-display font-semibold flex items-center gap-2">
          <RotateCcw className="h-5 w-5 text-rose-600" /> Stock-take reset
        </div>
      </div>
      <div className="panel-body space-y-4">
        <div className="rounded-lg border border-rose-200 bg-rose-50/40 dark:bg-rose-950/20 p-3 flex items-start gap-2">
          <AlertTriangle className="h-5 w-5 text-rose-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-rose-900 dark:text-rose-100 space-y-1">
            <div className="font-semibold">
              This zeroes current stock across every raw material.
            </div>
            <div className="text-rose-800 dark:text-rose-200">
              Every existing goods-intake row stays visible in Goods Intake and
              Trace &amp; Recall for compliance — only the <strong>remaining</strong>{" "}
              figure is set to <strong>0</strong>. After the reset, record today's
              physical stock as fresh new intakes. The reset is stamped with your
              name and timestamp on every affected row.
            </div>
            <div className="text-rose-800 dark:text-rose-200 pt-1">
              Open production batches are <strong>not blocked</strong> — close
              them yourself before resetting if they're mid-run.
            </div>
          </div>
        </div>

        {lastResult && (
          <div
            className="rounded-lg border border-border bg-stone-50 dark:bg-stone-900/50 p-3 text-sm flex items-center gap-2"
            data-testid="stock-reset-result"
          >
            <History className="h-4 w-4 text-stone-500" />
            Last reset in this session touched{" "}
            <strong>{lastResult.updated}</strong> intake rows.
          </div>
        )}

        <div>
          <Button
            variant="destructive"
            className="h-12 px-6"
            disabled={busy}
            onClick={() => setPinOpen(true)}
            data-testid="stock-reset-trigger"
          >
            {busy ? "Resetting…" : "Reset all stock to 0"}
          </Button>
          <div className="text-[11px] text-stone-500 mt-2">
            Requires admin PIN. Use the Dashboard's Raw material stock panel or
            Goods Intake page to re-enter today's physical counts.
          </div>
        </div>
      </div>

      <SupervisorPinModal
        open={pinOpen}
        onClose={() => setPinOpen(false)}
        roles={["admin"]}
        title="Admin PIN required"
        description="This will set every intake's remaining stock to 0. History is preserved."
        onVerified={() => {
          setPinOpen(false);
          doReset();
        }}
      />
    </div>
  );
}
